import { useEffect, useState } from "react";
import type {
  Project,
  Repository,
  SourceParameters,
  TestSelection,
  Values,
} from "@proof/core";
import { ImpersonationPicker, type PreviewUser } from "./ImpersonationPicker";
import { prepareImpersonation } from "./impersonation-session";
import { previewWindowFeatures } from "./native-preview";
import type { createWorkspaceTransport } from "./workspace-transport";
import {
  TestSelectionControls,
  initialTestSelection,
  useResolvedTestSelection,
  useTestSources,
} from "./TestSelectionControls";

export interface DesignerTrySnapshot {
  project: Project;
  values: Values;
  partId: string;
  demoProfile: "alex" | "jamie" | "guest";
  sourceParameters?: SourceParameters;
  /** Starting selection (Live proof's); otherwise the project default. */
  selection?: TestSelection;
  /** Where the window was opened from, for the dialog wording. */
  origin?: "designer" | "test" | "impersonation";
}

/**
 * Test this form / Impersonation / designer Try it out: choose a user plus the
 * published version, retained draft or mirrored form, then open a separate window.
 * Reuses the existing impersonation authority; never publishes or invents a preview identity.
 */
export function DesignerTryItOut({
  snapshot,
  repository,
  transport,
  onClose,
  onSaveDefault,
}: {
  snapshot: DesignerTrySnapshot;
  repository: Repository;
  transport: ReturnType<typeof createWorkspaceTransport>;
  onClose(): void;
  onSaveDefault?: (selection: TestSelection) => Promise<void>;
}) {
  const allowDrafts = transport.can("tenant.documents.manage");
  const { sources, error: sourceError } = useTestSources(
    repository,
    snapshot.project,
    allowDrafts,
  );
  const [users, setUsers] = useState<PreviewUser[]>(),
    [loadError, setLoadError] = useState(""),
    [launchError, setLaunchError] = useState(""),
    [chosen, setChosen] = useState<TestSelection | undefined>(
      snapshot.selection,
    ),
    [partId, setPartId] = useState(snapshot.partId);
  useEffect(() => {
    const controller = new AbortController();
    setUsers(undefined);
    setLoadError("");
    void transport.client
      .previewUsers(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setUsers(rows);
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setLoadError(
            `Could not load the impersonation preview: ${(error as Error).message}`,
          );
      });
    return () => controller.abort();
  }, [transport]);
  const selection =
    chosen ||
    initialTestSelection(
      snapshot.project,
      sources,
      allowDrafts,
      transport.demo,
    );
  const resolved = useResolvedTestSelection(
    selection,
    snapshot.project,
    sources,
    allowDrafts,
  );
  const definition = resolved?.project;
  useEffect(() => {
    if (definition && partId && !definition.parts.some((p) => p.id === partId))
      setPartId("");
  }, [definition, partId]);
  const unavailable =
    sources && !resolved
      ? "No published version is available. Publish this project first, then try it out. Your draft has not been changed."
      : "";
  const loading = (!users || !sources) && !loadError && !sourceError;
  return (
    <ImpersonationPicker
      users={users || []}
      loading={loading}
      blocked={!users || !resolved}
      error={loadError || sourceError || unavailable || launchError}
      description={
        snapshot.origin === "designer"
          ? transport.demo
            ? "Try your current draft in a separate proofing window. Changes in that window stay separate from the designer."
            : "Try a published version or saved draft in a separate proofing window, starting with its defaults and the selected user's profile. Unsaved designer changes are not included. Your selected user is checked by the existing impersonation flow."
          : "Open the proofing page in a new window with the selected version and form. Changes in that window stay separate from this workspace."
      }
      parts={definition?.parts}
      partId={partId}
      onPartChange={setPartId}
      onClose={onClose}
      onSelect={(subject) => {
        setLaunchError("");
        try {
          if (
            !users ||
            (subject !== null && !users.some((user) => user.id === subject))
          )
            throw new Error("Choose an available impersonation user.");
          if (!resolved) throw new Error("Choose a version to test first.");
          if (!transport.demo) {
            transport.openPreview(
              {
                projectId: snapshot.project.id,
                ...(resolved.versionId
                  ? { versionId: resolved.versionId }
                  : { testSelection: resolved.selection }),
                partId,
                values: snapshot.origin === "designer" ? {} : snapshot.values,
                sourceParameters:
                  snapshot.origin === "designer"
                    ? undefined
                    : snapshot.sourceParameters,
                previewSubject: subject,
              },
              { newWindow: true },
            );
          } else {
            const popup = window.open(
              "about:blank",
              "_blank",
              previewWindowFeatures(),
            );
            if (!popup)
              throw new Error(
                "Allow popups, then choose Open proofing page to try again.",
              );
            try {
              popup.opener = null;
              popup.location.replace(
                prepareImpersonation({
                  values: snapshot.values,
                  demoProfile: snapshot.demoProfile,
                  sourceParameters: snapshot.sourceParameters,
                  project: resolved.project,
                  // Omitted when the opened definition is the draft itself.
                  draft:
                    resolved.project === snapshot.project
                      ? undefined
                      : snapshot.project,
                  testSelection: resolved.selection,
                  partId,
                  previewSubject: subject,
                }),
              );
            } catch (error) {
              popup.close();
              throw error;
            }
          }
          onClose();
        } catch (error) {
          setLaunchError((error as Error).message);
        }
      }}
    >
      {sources && (
        <TestSelectionControls
          idPrefix="test-window"
          sources={sources}
          allowDrafts={allowDrafts}
          value={selection}
          onChange={(next) => {
            setChosen(next);
            setLaunchError("");
          }}
          current={snapshot.project}
          resolved={resolved}
          storedDefault={snapshot.project.settings.testSelection}
          onSaveDefault={allowDrafts ? onSaveDefault : undefined}
        />
      )}
    </ImpersonationPicker>
  );
}
